/**
 * CL-DRAWINGS-ORGANIZATION-001A — Drawing library organization (pure model).
 *
 * Canonical records: pmProjectFiles. No parallel collection.
 * Reuses drawing inventory, order, lifecycle, and publication helpers.
 */

import { normalizePmFileReviewStatus } from "./clientDrawingReviewGroups";
import { isOverallDrawingPackage } from "./drawingDocumentRoleModel";
import { isAdminManageableDrawingInventoryFile } from "./drawingInventoryModel";
import {
  compareDrawingFiles,
  drawingSortKeyForFile,
  parseDrawingSheetKey,
  sortDrawingsCanonical,
} from "./drawingOrderModel";
import { getCanonicalFileStatusBadge } from "./drawingWorkflowStatusBadge";
import {
  describeStoragePublicationAdminState,
  isStorageReadyForPortal,
} from "./projectFileStoragePublicationModel";
import {
  buildDrawingTaxonomyAssignmentPatch,
  fileMatchesDrawingTaxonomyFilters,
  getEffectiveDrawingTaxonomy,
  resolveDrawingTaxonomyPlacement,
  groupDrawingsByTaxonomy,
  listTaxonomyCategoryFilterOptions,
  listTaxonomySubcategoryFilterOptions,
} from "./drawingTaxonomyModel";
import {
  projectUsesTaxonomyDrawingOrder,
  sortDrawingsForTaxonomyPresentation,
} from "./drawingTaxonomyOrderModel";
import { isClientPortalIndividualDrawing } from "./drawingPortalIndividualEligibility";

function safeStr(value) {
  try {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  } catch {
    return "";
  }
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

function toMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    } catch {
      return 0;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

export const DRAWING_LIBRARY_VIEW = Object.freeze({
  CURRENT: "current",
  SUPERSEDED: "superseded",
  ARCHIVED: "archived",
});

export const DRAWING_LIBRARY_VIEW_LABEL = Object.freeze({
  [DRAWING_LIBRARY_VIEW.CURRENT]: "Current drawings",
  [DRAWING_LIBRARY_VIEW.SUPERSEDED]: "Superseded drawings",
  [DRAWING_LIBRARY_VIEW.ARCHIVED]: "Archived drawings",
});

export const DRAWING_LIBRARY_SORT = Object.freeze({
  DRAWING_NUMBER: "drawing_number",
  TITLE: "title",
  ISSUE_DATE: "issue_date",
  REVISION: "revision",
  UPDATED: "updated",
  CUSTOM_ORDER: "custom_order",
});

export const DRAWING_LIBRARY_UNCLASSIFIED = "Unclassified";

const DISCIPLINE_LABELS = Object.freeze({
  architectural: "Architectural",
  structural: "Structural",
  civil: "Civil / survey",
  mechanical: "Mechanical / HVAC",
  electrical: "Electrical",
  plumbing: "Plumbing",
  landscape: "Landscape / site works",
  general: "General",
});

export function resolveDrawingDiscipline(file = {}) {
  const raw =
    safeStr(file?.manualLibraryDiscipline) ||
    safeStr(file?.libraryDiscipline) ||
    safeStr(file?.discipline) ||
    "";
  const key = lower(raw);
  if (!key || key === "auto") return "";
  return DISCIPLINE_LABELS[key] || raw;
}

export function resolveDrawingDisciplineKey(file = {}) {
  const raw =
    safeStr(file?.manualLibraryDiscipline) ||
    safeStr(file?.libraryDiscipline) ||
    safeStr(file?.discipline) ||
    "";
  const key = lower(raw);
  if (!key || key === "auto") return "";
  return key;
}

/** Drawing set / package grouping from existing category metadata. */
export function resolveDrawingSetLabel(file = {}) {
  const sub = safeStr(file?.subcategory);
  if (sub && lower(sub) !== "none") return sub;
  const deliv = safeStr(file?.deliverableType);
  if (deliv) return deliv;
  const cat = safeStr(file?.category);
  if (cat && lower(cat) !== "drawings" && lower(cat) !== "uncategorized") return cat;
  return "";
}

export function resolveDrawingNumber(file = {}) {
  const explicit =
    safeStr(file?.sheetNumber) ||
    safeStr(file?.sheetCode) ||
    safeStr(file?.sheetNo) ||
    safeStr(file?.sheet) ||
    safeStr(file?.sheet_number);
  if (explicit) return explicit;
  const parsed = parseDrawingSheetKey(
    safeStr(file?.name) || safeStr(file?.displayTitle) || safeStr(file?.title)
  );
  if (parsed.coded) return parsed.compareLabel || parsed.raw;
  return "";
}

function drawingNumberLooksLikeFilename(value = "") {
  const raw = safeStr(value);
  if (!raw) return false;
  const lower = raw.toLowerCase();
  if (/\.(pdf|dwg|dxf|png|jpe?g|tif{1,2})\b/.test(lower)) return true;
  return raw.length > 48;
}

export function formatDrawingSheetKeyLabel(parsed = {}) {
  if (!parsed?.coded) return "";
  const discipline = safeStr(parsed.discipline);
  const number = parsed.number;
  const suffix = safeStr(parsed.suffix);
  if (discipline && number != null && Number.isFinite(number)) {
    return `${discipline}-${number}${suffix}`;
  }
  return safeStr(parsed.compareLabel) || safeStr(parsed.raw);
}

/**
 * Portal/admin card number line — canonical sheet code, not full filename/title.
 * Does not mutate stored metadata; search still uses resolveDrawingNumber on the file.
 */
export function formatPortalDrawingNumberDisplay(file = {}) {
  const probes = [
    safeStr(file?.sheetNumber),
    safeStr(file?.sheetCode),
    safeStr(file?.sheetNo),
    safeStr(file?.sheet),
    safeStr(file?.sheet_number),
    resolveDrawingTitle(file),
    safeStr(file?.name),
  ].filter(Boolean);

  for (let i = 0; i < probes.length; i += 1) {
    const probe = probes[i];
    const parsed = parseDrawingSheetKey(probe);
    if (parsed.coded) {
      const label = formatDrawingSheetKeyLabel(parsed);
      if (label) return label;
    }
    if (i < 5 && probe && !drawingNumberLooksLikeFilename(probe)) {
      return probe;
    }
  }
  return "";
}

export function resolveDrawingTitle(file = {}) {
  return (
    safeStr(file?.displayTitle) ||
    safeStr(file?.title) ||
    safeStr(file?.name) ||
    safeStr(file?.fileName) ||
    ""
  );
}

export function resolveDrawingRevision(file = {}) {
  return safeStr(file?.revision);
}

/** Collapsed-row revision label from canonical `file.revision` only (no filename inference). */
export function formatDrawingRevisionDisplay(file = {}) {
  const revision = resolveDrawingRevision(file);
  if (revision) return `Rev. ${revision}`;
  return "Revision not set";
}

export function resolveDrawingIssueDateMs(file = {}) {
  const issued = file?.issuedAt || file?.issueDate || file?.issuedOn;
  const ms = toMs(issued);
  return ms > 0 ? ms : 0;
}

export function resolveDrawingFileTypeLabel(file = {}) {
  const ct = lower(file?.contentType || file?.type || file?.mimeType);
  const name = lower(file?.name);
  if (ct.includes("pdf") || name.endsWith(".pdf")) return "PDF";
  if (ct.startsWith("image/")) return "Image";
  if (name.endsWith(".dwg") || name.endsWith(".dxf")) return "CAD";
  if (ct) return ct.split("/").pop().toUpperCase() || "File";
  return "";
}

export function isDeletedDrawingRecord(file = {}) {
  if (!file || file.deleted === true) return true;
  return lower(file?.status) === "deleted";
}

export function isArchivedDrawingRecord(file = {}) {
  if (isDeletedDrawingRecord(file)) return false;
  return lower(file?.status) === "archived" || lower(file?.documentStatus) === "archived";
}

export function isSupersededDrawingRecord(file = {}) {
  if (isDeletedDrawingRecord(file) || isArchivedDrawingRecord(file)) return false;
  return normalizePmFileReviewStatus(file?.reviewStatus) === "superseded";
}

export function classifyDrawingLibraryLifecycle(file = {}) {
  if (isDeletedDrawingRecord(file)) return "deleted";
  if (isArchivedDrawingRecord(file)) return DRAWING_LIBRARY_VIEW.ARCHIVED;
  if (isSupersededDrawingRecord(file)) return DRAWING_LIBRARY_VIEW.SUPERSEDED;
  return DRAWING_LIBRARY_VIEW.CURRENT;
}

export function isCurrentDrawingLibraryRecord(file = {}) {
  return classifyDrawingLibraryLifecycle(file) === DRAWING_LIBRARY_VIEW.CURRENT;
}

/**
 * Client portal: current, storage-ready, not admin-only hidden lifecycle states.
 * Authorization must still be applied by caller (shares); this is presentation filter only.
 */
export function isClientPortalCurrentDrawing(file = {}) {
  return isClientPortalIndividualDrawing(file);
}

function isDrawingReviewDomainFileForOrg(file) {
  return isAdminManageableDrawingInventoryFile(file);
}

export function buildDrawingLibraryRowPresentation(file = {}) {
  const lifecycle = classifyDrawingLibraryLifecycle(file);
  const drawingNumber = formatPortalDrawingNumberDisplay(file) || resolveDrawingNumber(file);
  const title = resolveDrawingTitle(file);
  const discipline = resolveDrawingDiscipline(file);
  const setLabel = resolveDrawingSetLabel(file);
  const revision = resolveDrawingRevision(file);
  const issueMs = resolveDrawingIssueDateMs(file);
  const fileType = resolveDrawingFileTypeLabel(file);
  const reviewBadge = getCanonicalFileStatusBadge(file);
  const publication = describeStoragePublicationAdminState(file);
  const portalVisible = isStorageReadyForPortal(file);

  return {
    fileId: safeStr(file?.id),
    drawingNumber,
    title,
    discipline,
    disciplineKey: resolveDrawingDisciplineKey(file) || DRAWING_LIBRARY_UNCLASSIFIED,
    setLabel,
    revision,
    issueDateMs: issueMs,
    issueDateLabel: issueMs > 0 ? new Date(issueMs).toLocaleDateString() : "",
    fileType,
    reviewStatusLabel: reviewBadge.label,
    reviewStatusTone: reviewBadge.tone,
    portalVisible,
    portalPublicationLabel: publication.label,
    lifecycle,
    lifecycleLabel:
      lifecycle === DRAWING_LIBRARY_VIEW.CURRENT
        ? "Current"
        : lifecycle === DRAWING_LIBRARY_VIEW.SUPERSEDED
          ? "Superseded"
          : lifecycle === DRAWING_LIBRARY_VIEW.ARCHIVED
            ? "Archived"
            : "",
    filename: safeStr(file?.name),
    updatedAtMs: toMs(file?.updatedAt || file?.mainFileReplacedAt || file?.createdAt),
  };
}

export function buildDrawingLibrarySearchHaystack(file = {}, taxonomy = null) {
  const row = buildDrawingLibraryRowPresentation(file);
  const placement = taxonomy ? resolveDrawingTaxonomyPlacement(file, taxonomy) : null;
  const normalizedNumber = normalizeDrawingNumberForMatch(row.drawingNumber);
  return [
    row.drawingNumber,
    normalizedNumber,
    row.title,
    row.filename,
    row.discipline,
    row.setLabel,
    placement?.categoryName,
    placement?.subcategoryName,
    safeStr(file?.category),
    safeStr(file?.subcategory),
    safeStr(file?.manualLibraryDiscipline),
    safeStr(file?.drawingCategoryId),
    safeStr(file?.drawingSubcategoryId),
  ]
    .join(" ")
    .toLowerCase();
}

function drawingLibrarySearchTokens(search = "") {
  const q = lower(search).trim();
  if (!q) return [];
  return q.split(/\s+/).filter(Boolean);
}

export function drawingMatchesLibrarySearch(file = {}, search = "", taxonomy = null) {
  const tokens = drawingLibrarySearchTokens(search);
  if (!tokens.length) return true;
  const haystack = buildDrawingLibrarySearchHaystack(file, taxonomy);
  const fileNumberNorm = normalizeDrawingNumberForMatch(resolveDrawingNumber(file));
  return tokens.every((token) => {
    if (haystack.includes(token)) return true;
    const tokenNorm = normalizeDrawingNumberForMatch(token);
    if (tokenNorm && fileNumberNorm && fileNumberNorm.includes(tokenNorm)) return true;
    return false;
  });
}

export function filterDrawingsForLibrary(
  files = [],
  {
    view = DRAWING_LIBRARY_VIEW.CURRENT,
    search = "",
    disciplineKey = "all",
    categoryKey = "all",
    subcategoryKey = "all",
    taxonomy = null,
    lifecycleFilter = "all",
    portalVisibility = "all",
  } = {}
) {
  const disc = lower(disciplineKey);
  const vis = lower(portalVisibility);
  const effectiveTaxonomy = taxonomy || null;

  return (Array.isArray(files) ? files : []).filter((file) => {
    if (!file || !isDrawingReviewDomainFileForOrg(file)) return false;
    if (isDeletedDrawingRecord(file)) return false;
    if (isOverallDrawingPackage(file)) return false;

    const lifecycle = classifyDrawingLibraryLifecycle(file);
    const targetView =
      lifecycleFilter !== "all" ? lifecycleFilter : view;
    if (lifecycle !== targetView) return false;

    if (disc !== "all") {
      const key = resolveDrawingDisciplineKey(file);
      if (disc === DRAWING_LIBRARY_UNCLASSIFIED.toLowerCase()) {
        if (key) return false;
      } else if (key !== disc) {
        return false;
      }
    }

    if (vis === "published" && !isStorageReadyForPortal(file)) return false;
    if (vis === "unpublished" && isStorageReadyForPortal(file)) return false;

    if (!drawingMatchesLibrarySearch(file, search, effectiveTaxonomy)) return false;

    if (effectiveTaxonomy) {
      return fileMatchesDrawingTaxonomyFilters(file, effectiveTaxonomy, {
        categoryKey,
        subcategoryKey,
      });
    }
    return true;
  });
}

export function mapTaxonomyGroupedToLibraryShape(taxonomyGrouped = {}) {
  return {
    disciplines: (taxonomyGrouped.categories || []).map((cat) => ({
      id: cat.id,
      label: cat.label,
      count: cat.count,
      sets: (cat.subcategories || []).map((sub) => ({
        id: sub.id,
        categoryId: cat.id,
        subcategoryId: sub.id,
        label: sub.label,
        files: sub.files,
        count: sub.count,
      })),
    })),
    total: taxonomyGrouped.total || 0,
  };
}

function compareBySortKey(a, b, sortKey) {
  const key = lower(sortKey) || DRAWING_LIBRARY_SORT.DRAWING_NUMBER;
  if (key === DRAWING_LIBRARY_SORT.TITLE) {
    return resolveDrawingTitle(a).localeCompare(resolveDrawingTitle(b), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }
  if (key === DRAWING_LIBRARY_SORT.REVISION) {
    return resolveDrawingRevision(a).localeCompare(resolveDrawingRevision(b), undefined, {
      sensitivity: "base",
      numeric: true,
    });
  }
  if (key === DRAWING_LIBRARY_SORT.ISSUE_DATE) {
    return resolveDrawingIssueDateMs(b) - resolveDrawingIssueDateMs(a);
  }
  if (key === DRAWING_LIBRARY_SORT.UPDATED) {
    return toMs(b?.updatedAt || b?.mainFileReplacedAt) - toMs(a?.updatedAt || a?.mainFileReplacedAt);
  }
  return compareDrawingFiles(a, b);
}

export function sortDrawingsForLibrary(files = [], sortKey = DRAWING_LIBRARY_SORT.DRAWING_NUMBER) {
  return [...(Array.isArray(files) ? files : [])].sort((a, b) => compareBySortKey(a, b, sortKey));
}

/**
 * Group: discipline → drawing set → files (unclassified bucket explicit).
 */
export function groupDrawingsForLibrary(files = [], { sortKey = DRAWING_LIBRARY_SORT.DRAWING_NUMBER } = {}) {
  const sorted = sortDrawingsForLibrary(files, sortKey);
  const disciplineMap = new Map();

  sorted.forEach((file) => {
    const disciplineKey = resolveDrawingDisciplineKey(file);
    const disciplineLabel = disciplineKey ? resolveDrawingDiscipline(file) : DRAWING_LIBRARY_UNCLASSIFIED;
    const setLabel = resolveDrawingSetLabel(file) || DRAWING_LIBRARY_UNCLASSIFIED;

    if (!disciplineMap.has(disciplineLabel)) {
      disciplineMap.set(disciplineLabel, { label: disciplineLabel, sets: new Map(), files: [] });
    }
    const discGroup = disciplineMap.get(disciplineLabel);
    if (!discGroup.sets.has(setLabel)) {
      discGroup.sets.set(setLabel, { label: setLabel, files: [] });
    }
    discGroup.sets.get(setLabel).files.push(file);
  });

  const disciplines = [...disciplineMap.values()].map((d) => ({
    label: d.label,
    sets: [...d.sets.values()].map((s) => ({
      label: s.label,
      files: s.files,
      count: s.files.length,
    })),
    count: [...d.sets.values()].reduce((n, s) => n + s.files.length, 0),
  }));

  disciplines.sort((a, b) => {
    if (a.label === DRAWING_LIBRARY_UNCLASSIFIED) return 1;
    if (b.label === DRAWING_LIBRARY_UNCLASSIFIED) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });

  return { disciplines, total: sorted.length };
}

export function listDrawingDisciplineFilterOptions(files = []) {
  const keys = new Map();
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (!file || isOverallDrawingPackage(file)) return;
    const k = resolveDrawingDisciplineKey(file);
    if (k) keys.set(k, resolveDrawingDiscipline(file));
  });
  const options = [...keys.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  options.unshift({ value: "all", label: "All disciplines" });
  options.push({ value: DRAWING_LIBRARY_UNCLASSIFIED.toLowerCase(), label: DRAWING_LIBRARY_UNCLASSIFIED });
  return options;
}

export function buildAdminDrawingLibraryViewModel({
  projectFiles = [],
  project = null,
  view = DRAWING_LIBRARY_VIEW.CURRENT,
  search = "",
  disciplineKey = "all",
  categoryKey = "all",
  subcategoryKey = "all",
  portalVisibility = "all",
  sortKey = DRAWING_LIBRARY_SORT.DRAWING_NUMBER,
} = {}) {
  const inventory = (Array.isArray(projectFiles) ? projectFiles : []).filter(
    (f) => isAdminManageableDrawingInventoryFile(f) && !isOverallDrawingPackage(f)
  );
  const taxonomy = getEffectiveDrawingTaxonomy(project, inventory);
  const filteredBase = filterDrawingsForLibrary(inventory, {
    view,
    search,
    disciplineKey,
    categoryKey,
    subcategoryKey,
    taxonomy,
    portalVisibility,
  });
  const filtered =
    lower(sortKey) === DRAWING_LIBRARY_SORT.CUSTOM_ORDER
      ? sortDrawingsForTaxonomyPresentation(filteredBase, taxonomy, { project })
      : sortDrawingsForLibrary(filteredBase, sortKey);
  const taxonomyGrouped = groupDrawingsByTaxonomy(filtered, taxonomy, { project });
  const grouped = mapTaxonomyGroupedToLibraryShape(taxonomyGrouped);
  const categoryOptions = listTaxonomyCategoryFilterOptions(taxonomy);
  const subcategoryOptions = listTaxonomySubcategoryFilterOptions(taxonomy, categoryKey);
  return {
    view,
    filtered,
    grouped,
    taxonomy,
    count: filtered.length,
    categoryOptions,
    subcategoryOptions,
    disciplineOptions: listDrawingDisciplineFilterOptions(inventory),
    inventoryCount: inventory.length,
    canonicalOrder: sortDrawingsCanonical(inventory, project, { includeExcluded: true }),
  };
}

export function flattenClientDrawingLibraryFileIds(viewModel = {}) {
  const ids = [];
  (viewModel?.grouped?.disciplines || []).forEach((disc) => {
    (disc.sets || []).forEach((set) => {
      (set.files || []).forEach((file) => {
        const id = safeStr(file?.id);
        if (id) ids.push(id);
      });
    });
  });
  return ids;
}

/** Development invariant: visible count matches rendered individual hierarchy. */
export function clientDrawingLibraryCountMatchesRendered(viewModel = {}) {
  const flat = flattenClientDrawingLibraryFileIds(viewModel);
  const count = Math.max(0, Number(viewModel?.count) || 0);
  return count === flat.length && new Set(flat).size === flat.length;
}

export function buildClientDrawingLibraryViewModel({
  authorizedFiles = [],
  project = null,
  sortKey = DRAWING_LIBRARY_SORT.DRAWING_NUMBER,
} = {}) {
  const current = (Array.isArray(authorizedFiles) ? authorizedFiles : []).filter((f) =>
    isClientPortalCurrentDrawing(f)
  );
  const taxonomy = getEffectiveDrawingTaxonomy(project, current);
  const ordered = projectUsesTaxonomyDrawingOrder(project, current)
    ? sortDrawingsForTaxonomyPresentation(current, taxonomy, { project, includeExcluded: false })
    : sortDrawingsCanonical(current, project, { includeExcluded: false });
  const taxonomyGrouped = groupDrawingsByTaxonomy(ordered, taxonomy, { project });
  const grouped = mapTaxonomyGroupedToLibraryShape(taxonomyGrouped);
  const viewModel = {
    files: ordered,
    grouped,
    taxonomy,
    count: ordered.length,
  };
  if (process.env.NODE_ENV !== "production" && !clientDrawingLibraryCountMatchesRendered(viewModel)) {
    // eslint-disable-next-line no-console
    console.warn("[drawings] client library count does not match rendered hierarchy");
  }
  return viewModel;
}

export function buildDrawingArchivePatch() {
  return {
    status: "archived",
    documentStatus: "archived",
  };
}

export function buildDrawingRestorePatch() {
  return {
    status: "active",
    documentStatus: null,
  };
}

/** Move a current drawing to Superseded (retains record, storage, and revision history). */
export function buildDrawingSupersedePatch() {
  return {
    reviewStatus: "superseded",
  };
}

/** Normalize drawing numbers for current/superseded matching (A-101 / A101). */
export function normalizeDrawingNumberForMatch(drawingNumber = "") {
  const raw = safeStr(drawingNumber);
  if (!raw) return "";
  const parsed = parseDrawingSheetKey(raw);
  if (parsed.coded && parsed.normalized) return parsed.normalized.toUpperCase();
  return raw.replace(/[\s_\-.]+/g, "").toUpperCase();
}

/**
 * Find inventory files with the same normalized drawing number (current + superseded).
 * @returns {{ file: object, lifecycle: string }[]}
 */
export function findDrawingInventoryMatchesByNumber(inventory = [], drawingNumber = "") {
  const needle = normalizeDrawingNumberForMatch(drawingNumber);
  if (!needle) return [];
  return (Array.isArray(inventory) ? inventory : [])
    .map((file) => {
      if (!file || isOverallDrawingPackage(file)) return null;
      const num = normalizeDrawingNumberForMatch(resolveDrawingNumber(file));
      if (!num || num !== needle) return null;
      return { file, lifecycle: classifyDrawingLibraryLifecycle(file) };
    })
    .filter(Boolean);
}

export function summarizeDrawingSelection({
  selectedIds = [],
  filteredFiles = [],
} = {}) {
  const selected = [...new Set((Array.isArray(selectedIds) ? selectedIds : []).map((id) => safeStr(id)).filter(Boolean))];
  const visibleIds = new Set(
    (Array.isArray(filteredFiles) ? filteredFiles : []).map((f) => safeStr(f?.id)).filter(Boolean)
  );
  const visibleSelectedCount = selected.filter((id) => visibleIds.has(id)).length;
  return {
    totalSelectedCount: selected.length,
    visibleSelectedCount,
    hiddenSelectedCount: Math.max(0, selected.length - visibleSelectedCount),
  };
}

export function selectAllShownDrawingIds(filteredFiles = []) {
  return (Array.isArray(filteredFiles) ? filteredFiles : [])
    .map((f) => safeStr(f?.id))
    .filter(Boolean);
}

export function formatBulkSupersedeResultMessage(summary = {}) {
  const n = summary.successCount || 0;
  const f = summary.failureCount || 0;
  if (f === 0) {
    return `Moved ${n} drawing${n === 1 ? "" : "s"} to Superseded drawings. Files and revision history were retained.`;
  }
  if (n === 0) return `Could not move ${f} drawing${f === 1 ? "" : "s"}.`;
  return `Moved ${n} drawing${n === 1 ? "" : "s"} to Superseded; ${f} failed.`;
}

export function buildDrawingBulkMetadataPatch({
  discipline = "",
  category = "",
  subcategory = "",
  revision = "",
  displayTitle = "",
  drawingNumber = "",
  drawingCategoryId = "",
  drawingSubcategoryId = "",
  taxonomy = null,
} = {}) {
  const patch = {};
  const d = lower(discipline);
  if (d && d !== "unchanged") {
    patch.manualLibraryDiscipline = d === "clear" ? null : d;
    patch.discipline = d === "clear" ? null : d;
  }
  if (safeStr(category) && category !== "unchanged") patch.category = safeStr(category);
  if (safeStr(subcategory) && subcategory !== "unchanged") patch.subcategory = safeStr(subcategory);
  if (revision !== "unchanged") patch.revision = safeStr(revision) || null;
  if (displayTitle !== "unchanged") patch.displayTitle = safeStr(displayTitle) || null;
  if (drawingNumber !== "unchanged") {
    patch.sheetNumber = safeStr(drawingNumber) || null;
  }
  if (drawingCategoryId && drawingCategoryId !== "unchanged" && taxonomy) {
    Object.assign(
      patch,
      buildDrawingTaxonomyAssignmentPatch({
        categoryId: drawingCategoryId,
        subcategoryId: drawingSubcategoryId,
        taxonomy,
      })
    );
  }
  return patch;
}

export function buildDrawingSingleEditPatch(fields = {}) {
  return buildDrawingBulkMetadataPatch(fields);
}

/**
 * Normalize Admin drawing-library bulk/single-edit form fields to a Firestore pmProjectFiles patch.
 * Direct patches (discipline-only bulk, archive, etc.) pass through when already Firestore-shaped.
 */
export function resolveDrawingLibraryBulkUpdatePatch(metadataFieldsOrPatch = {}, taxonomy = null) {
  const fields =
    metadataFieldsOrPatch && typeof metadataFieldsOrPatch === "object" ? metadataFieldsOrPatch : {};
  const isDirectFirestorePatch =
    Object.prototype.hasOwnProperty.call(fields, "manualLibraryDiscipline") ||
    Object.prototype.hasOwnProperty.call(fields, "sheetNumber") ||
    (Object.prototype.hasOwnProperty.call(fields, "status") &&
      !Object.prototype.hasOwnProperty.call(fields, "drawingNumber") &&
      !Object.prototype.hasOwnProperty.call(fields, "drawingCategoryId"));
  if (isDirectFirestorePatch) {
    return { ...fields };
  }
  return buildDrawingBulkMetadataPatch({ ...fields, taxonomy });
}

/**
 * Pure bulk result aggregator for tests and UI messaging.
 */
export function aggregateBulkDrawingResults(results = []) {
  const succeeded = [];
  const failed = [];
  (Array.isArray(results) ? results : []).forEach((row) => {
    if (row?.ok) succeeded.push(row);
    else failed.push(row);
  });
  return {
    ok: failed.length === 0,
    partial: succeeded.length > 0 && failed.length > 0,
    succeeded,
    failed,
    successCount: succeeded.length,
    failureCount: failed.length,
  };
}

export function formatBulkDrawingResultMessage(summary = {}) {
  const n = summary.successCount || 0;
  const f = summary.failureCount || 0;
  if (f === 0) return `Updated ${n} drawing${n === 1 ? "" : "s"}.`;
  if (n === 0) return `All ${f} drawing${f === 1 ? "" : "s"} failed.`;
  return `${n} drawing${n === 1 ? "" : "s"} updated; ${f} failed.`;
}
